import { useEffect, useRef, useState } from 'react';
import { Text, Line, Group } from 'react-konva';
import { ENGRAVING_GOLD_COLOR, isEngravingGold } from 'src/constants/pattern';
import { Vector3 } from 'three';
import * as THREE from 'three';

type KanvasEngravingProps = {
  pattern: {
    inverse_horizontal: boolean;
    inverse_vertical: boolean;
    position: Vector3;
    pieceUUID: string;
    orientation: string;
    uuid: string;
    text: string;
    fontSize: string;
    letterSpacing: string;
    fontFamily: string;
    align: string;
    color: string;
  };
  stageRef: React.MutableRefObject<any>;
  orientation: {
    key: 'x' | 'y' | 'z' | 'free';
    inverse: boolean;
  };
  setPatterns: (patterns: { [key: string]: { [key: string]: any }[] }) => void;
  patterns: {
    [key: string]: { [key: string]: any }[];
  };
  pieceUUID: string;
  setSelectedPattern: (selectedPattern: number | undefined) => void;
  patternIndex: number;
  orbitRef: React.MutableRefObject<any>;
  ratio: number | undefined;
  kanvasSize: { width: number; height: number } | undefined;
};

export const KanvasEngraving = ({
  pattern,
  orientation,
  setPatterns,
  patterns,
  pieceUUID,
  stageRef,
  setSelectedPattern,
  patternIndex,
  orbitRef,
  ratio,
  kanvasSize,
}: KanvasEngravingProps) => {
  const shapeRef = useRef<any>();
  const textRef = useRef<any>();
  const [showGuides, setShowGuides] = useState<boolean>(false);
  const [guidePosition, setGuidePosition] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });

  const changePattern = (position?: Vector3) => {
    const patternsTmp = { ...patterns };
    const currentOrientation = `${orientation.key}${
      orientation.inverse ? '_inverse' : ''
    }`;

    patternsTmp[pattern.pieceUUID][currentOrientation].elements = patterns[
      pattern.pieceUUID
    ][currentOrientation].elements.map((element: any) => {
      if (element.uuid === pattern.uuid) {
        return {
          ...element,
          position: position || element.position,
        };
      }
      return element;
    });

    if (stageRef.current && patternsTmp[pieceUUID][currentOrientation]) {
      const canvas = stageRef.current.toCanvas();
      const texture = new THREE.CanvasTexture(canvas);
      patternsTmp[pieceUUID][currentOrientation].texture = texture;

      patternsTmp[pieceUUID][currentOrientation].camera =
        orbitRef.current.object.clone();
    }

    setPatterns(patternsTmp);
  };

  const handleDragMove = (e: any) => {
    if (!kanvasSize) return;

    const node = shapeRef.current;
    const stage = node.getStage();
    const pointerPosition = stage.getPointerPosition();

    // Calculer la position du centre du canvas
    const centerX = kanvasSize.width / 2;
    const centerY = kanvasSize.height / 2;

    // Vérifier si le texte est proche du centre
    const threshold = 20; // Distance en pixels pour activer le guidage
    const isNearCenterX = Math.abs(pointerPosition.x - centerX) < threshold;
    const isNearCenterY = Math.abs(pointerPosition.y - centerY) < threshold;

    if (isNearCenterX || isNearCenterY) {
      setShowGuides(true);
      setGuidePosition({ x: centerX, y: centerY });

      // Appliquer l'effet d'aimantation
      if (isNearCenterX) {
        node.x(centerX - (node.width() * node.scaleX()) / 2);
      }
      if (isNearCenterY) {
        node.y(centerY - (node.height() * node.scaleY()) / 2);
      }
    } else {
      setShowGuides(false);
    }
  };

  const handleDragEnd = (e: any) => {
    setShowGuides(false);
    const node = shapeRef.current;

    // Vérifier si le texte est proche du centre pour l'aimantation finale
    if (kanvasSize) {
      const centerX = kanvasSize.width / 2;
      const centerY = kanvasSize.height / 2;
      const threshold = 20;

      const finalX =
        Math.abs(node.x() - centerX) < threshold ? centerX : node.x();
      const finalY =
        Math.abs(node.y() - centerY) < threshold ? centerY : node.y();

      changePattern(new Vector3(Math.round(finalX), Math.round(finalY), 0));
    }
  };

  const sizeWithRatio = (cm: number) => {
    return cm * (ratio ?? 1) * 1.4;
  };

  const fontSize = sizeWithRatio(
    Number.isNaN(Number.parseInt(pattern.fontSize, 10))
      ? 0.3
      : Number.parseInt(pattern.fontSize, 10),
  );

  const letterSpacing = sizeWithRatio(
    Number.isNaN(Number.parseInt(pattern.letterSpacing, 10))
      ? 0
      : Number.parseInt(pattern.letterSpacing, 10),
  );

  const isGold = isEngravingGold(pattern.color);

  // Le reflet blanc doit rester *dans* la lettre, pas baver sur le granit : on
  // le confine avec un 'source-atop' isolé dans le cache du groupe. L'ombre est
  // produite seule en envoyant son émetteur hors du cadre mis en cache ; seule
  // l'ombre, ramenée par shadowOffsetX, retombe dans le cadre.
  const CASTER_OFFSET = 5000;
  const reliefOffsetX = fontSize * 0.04;
  const reliefOffsetY = fontSize * 0.1;
  const reliefBlur = fontSize * 0.1;

  // Konva ne connaît la taille du texte qu'après rendu : on (re)cache dès que
  // la mise en page change.
  useEffect(() => {
    const group = shapeRef.current;
    const textNode = textRef.current;
    if (!group || !textNode) return;

    const width = Math.ceil(textNode.width());
    const height = Math.ceil(textNode.height());

    group.clearCache();
    // un Group ne déduit pas sa taille de ses enfants, or l'aimantation au
    // centre lit group.width()/height() : il faut la poser explicitement.
    group.size({ width, height });

    if (isGold && width > 0 && height > 0) {
      group.cache({ x: 0, y: 0, width, height, pixelRatio: 2 });
    }

    // la mise en cache a lieu apres le rendu de react-konva : sans redessin
    // explicite, le relief n'apparaitrait qu'au prochain mouvement.
    group.getLayer()?.batchDraw();
  }, [
    isGold,
    pattern.text,
    pattern.fontFamily,
    pattern.align,
    pattern.color,
    fontSize,
    letterSpacing,
  ]);

  return (
    <>
      {showGuides && (
        <>
          <Line
            points={[
              guidePosition.x,
              0,
              guidePosition.x,
              kanvasSize?.height ?? 0,
            ]}
            stroke='#ffffff'
            strokeWidth={1}
            dash={[5, 5]}
            shadowColor='#ffffff'
            shadowBlur={10}
            shadowOffset={{ x: 0, y: 0 }}
            shadowOpacity={0.8}
          />
          <Line
            points={[
              0,
              guidePosition.y,
              kanvasSize?.width ?? 0,
              guidePosition.y,
            ]}
            stroke='#ffffff'
            strokeWidth={1}
            dash={[5, 5]}
            shadowColor='#ffffff'
            shadowBlur={10}
            shadowOffset={{ x: 0, y: 0 }}
            shadowOpacity={1}
          />
        </>
      )}
      <Group
        onClick={() => setSelectedPattern(patternIndex)}
        onTap={() => setSelectedPattern(patternIndex)}
        ref={shapeRef}
        x={pattern.position.x}
        y={pattern.position.y}
        draggable
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
        onMouseEnter={(e) => {
          const container = e.target?.getStage()?.container();
          if (container) container.style.cursor = 'pointer';
        }}
        onMouseLeave={(e) => {
          const container = e.target?.getStage()?.container();
          if (container) container.style.cursor = 'default';
        }}
      >
        <Text
          ref={textRef}
          x={0}
          y={0}
          fontSize={fontSize}
          letterSpacing={letterSpacing}
          fontFamily={pattern.fontFamily}
          align={pattern.align}
          fill={isGold ? ENGRAVING_GOLD_COLOR : pattern.color}
          text={pattern.text}
        />
        {isGold ? (
          <Text
            x={-CASTER_OFFSET}
            y={0}
            fontSize={fontSize}
            letterSpacing={letterSpacing}
            fontFamily={pattern.fontFamily}
            align={pattern.align}
            fill='#000000'
            text={pattern.text}
            shadowColor='#FFFFFF'
            shadowOffsetX={CASTER_OFFSET + reliefOffsetX}
            shadowOffsetY={reliefOffsetY}
            shadowBlur={reliefBlur}
            shadowOpacity={0.75}
            globalCompositeOperation='source-atop'
            listening={false}
          />
        ) : null}
      </Group>
    </>
  );
};
